import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink, useNavigate } from 'react-router-dom';
import type { PropsWithChildren } from 'react';
import { api, ApiError } from '@/lib/api';

type AppShellProps = PropsWithChildren<{
  compact?: boolean;
  adminMode?: boolean;
}>;

export function AppShell({ children, compact = false, adminMode = false }: AppShellProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    enabled: !adminMode,
  });

  const logoutMutation = useMutation({
    mutationFn: api.participantLogout,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['agenda'] }),
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
      ]);
      navigate('/agenda');
    },
  });

  const isLoggedIn = meQuery.isSuccess;
  const isLoggedOut = meQuery.error instanceof ApiError && meQuery.error.status === 401;

  return (
    <div className={`shell ${compact ? 'shell--compact' : ''}`}>
      <div className="shell__texture shell__texture--one" />
      <div className="shell__texture shell__texture--two" />
      {!adminMode && (
        <nav className="top-nav">
          <NavLink to="/agenda" className={({ isActive }) => (isActive ? 'top-nav__link is-active' : 'top-nav__link')}>
            Agenda
          </NavLink>
          <NavLink to="/my-plan" className={({ isActive }) => (isActive ? 'top-nav__link is-active' : 'top-nav__link')}>
            Mein Plan
          </NavLink>
          {isLoggedIn ? (
            <button className="top-nav__button top-nav__button--active" type="button" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              {logoutMutation.isPending ? 'Log-out…' : 'Log-out'}
            </button>
          ) : (
            <NavLink to="/login" className={({ isActive }) => (isActive ? 'top-nav__link is-active' : 'top-nav__link')}>
              Login
            </NavLink>
          )}
        </nav>
      )}
      <header className="hero">
        <div className="hero__eyebrow">!GROW 2026</div>
        <h1>{adminMode ? 'ADMIN STUDIO' : 'PROGRAMMHEFT'}</h1>
        <p>
          {adminMode
            ? 'Tabellen hochladen, Vorschau prüfen und das Event ohne manuelle Nachpflege live halten.'
            : 'Finde deine Tracks, melde dich pro Slot genau einmal an und behalte deinen persönlichen Plan im Blick.'}
        </p>
      </header>

      <main className="shell__content">{children}</main>
    </div>
  );
}
